import {forwardRef} from 'react'
import {type TextProps} from 'react-native'
import Svg, {
  Circle,
  Defs,
  G,
  LinearGradient,
  Mask,
  Path,
  type PathProps,
  Rect,
  Stop,
  type SvgProps,
} from 'react-native-svg'
import {Image} from 'expo-image'

import {useLogoVariant} from '#/view/icons/useLogoVariant'
import {flatten, useTheme} from '#/alf'

/*
 * Source artwork is 771.36444 x 843.41077, so the logo is taller than it is
 * wide. Callers size by width, so height has to be derived from this - it is
 * not square, and it is the inverse of the butterfly that preceded it.
 */
const VB_WIDTH = 771.36444
const VB_HEIGHT = 843.41077
const ratio = VB_HEIGHT / VB_WIDTH

/*
 * The paint wells are knocked out of the body with a mask rather than painted
 * a background color, so whatever sits behind the logo shows through them. That
 * keeps the single-fill contract every call site relies on - a logo over the
 * QR card gradient or the splash background needs no special handling.
 */
const WELL_RADIUS = 61.44
const WELLS = [
  {cx: 361.73984, cy: 255.32932},
  {cx: 611.59985, cy: 361.81934},
  {cx: 664.84984, cy: 513.36932},
  {cx: 206.08984, cy: 329.04932},
  {cx: 111.88984, cy: 468.31932},
]

type Props = {
  allowVariants?: boolean
  fill?: PathProps['fill']
  style?: TextProps['style']
} & Omit<SvgProps, 'style'>

export const Logo = forwardRef(function LogoImpl(props: Props, ref) {
  const t = useTheme()
  const {allowVariants = true, fill, ...rest} = props
  const gradient = fill === 'sky'
  const styles = flatten(props.style)
  const _fill = gradient
    ? 'url(#sky)'
    : fill || styles?.color || t.palette.primary_500
  // @ts-ignore it's fiiiiine
  const size = parseInt(rest.width || 32, 10)

  const logoVariant = useLogoVariant(allowVariants)

  if (logoVariant !== 'default') {
    const isJapanLogo = logoVariant === 'japan'
    return (
      <Image
        source={
          isJapanLogo
            ? require('../../../assets/icons/custom_logo_japan.svg')
            : size > 100
              ? require('../../../assets/kawaii.png')
              : require('../../../assets/kawaii_smol.png')
        }
        accessibilityLabel="Skeetch"
        accessibilityHint=""
        accessibilityIgnoresInvertColors
        style={[{height: size, aspectRatio: isJapanLogo ? 2 : 1.4}]}
      />
    )
  }

  return (
    <Svg
      fill="none"
      // @ts-ignore it's fiiiiine
      ref={ref}
      viewBox={`0 0 ${VB_WIDTH} ${VB_HEIGHT}`}
      {...rest}
      style={[{width: size, height: size * ratio}, styles]}>
      <Defs>
        {gradient && (
          <LinearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="#0A7AFF" stopOpacity="1" />
            <Stop offset="1" stopColor="#59B9FF" stopOpacity="1" />
          </LinearGradient>
        )}
        <Mask
          id="skeetchWells"
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width={VB_WIDTH}
          height={VB_HEIGHT}>
          <Rect x="0" y="0" width={VB_WIDTH} height={VB_HEIGHT} fill="#fff" />
          {WELLS.map(well => (
            <Circle
              key={`${well.cx},${well.cy}`}
              cx={well.cx}
              cy={well.cy}
              r={WELL_RADIUS}
              fill="#000"
            />
          ))}
        </Mask>
      </Defs>

      <G mask="url(#skeetchWells)">
        <Path
          fillRule="evenodd"
          fill={_fill}
          d="m 279.50984,613.91932 c -102.59,13.44 -238.680002,52.64 -274.120002,-104.64 -42.27,-187.58 171.460002,-348.16 382.980002,-348.16 32.89,0 71.59,5.34 102.14,12.43"
        />
        <Path
          fillRule="evenodd"
          fill={_fill}
          d="m 578.11984,206.51932 c 116.25,59.91 194.84,173.05 193.22,302.76 -4.09,327.68 -425.32,412.82 -385.02,262.14 21.82,-81.59 12.56,-124.16 -9.14,-144.54"
        />
        <Path
          fillRule="evenodd"
          fill={_fill}
          d="m 525.54984,175.29932 c 7.11,-14.75 19.47,-23.65 27.61,-19.87 8.13,3.77 8.96,18.78 1.84,33.53 l -272.66,565.3 c -7.11,14.75 -19.47,23.65 -27.61,19.88 -8.13,-3.77 -8.96,-18.79 -1.84,-33.54 z"
        />
        <Path
          fillRule="evenodd"
          fill={_fill}
          d="m 547.17984,102.21932 c 25.48,-81.819998 83.94,-104.3299977 76.97,-92.6599977 -40.97,68.6599997 -8.14,49.6399997 -24.31,113.3199977 -8.09,31.89 -23.39,1.43 -35.74,-3.86 -12.36,-5.3 -26.69,14.55 -16.92,-16.8 z"
        />
      </G>
    </Svg>
  )
})
